import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { EWSS_GRADES } from "../constants/registerFilters";
import { getWordEwssDisplayGrade } from "../utils/ewssGradeDisplay";
import MatrixPoolScopeDropdown from "./MatrixPoolScopeDropdown";
import "./GameProgressChart.css";
import "./MasteryMatrixChart.css";

const GRADE_COUNT = EWSS_GRADES.length; // 12 (A+ … D-)
const IDX_BPLUS = EWSS_GRADES.indexOf("B+");

// Bande où l'on regroupe les mots non encore évalués (grade indisponible).
const UNRATED_Y = -1.8;

const CHART_MARGIN = { top: 8, right: 16, bottom: 10, left: 8 };
const Y_AXIS_WIDTH = 34;
const PINNED_CURSOR_STROKE = "rgba(57, 33, 13, 0.25)";
const PINNED_CURSOR_DASH = "3 3";
const PINNED_TOOLTIP_WIDTH = 168;
const PINNED_TOOLTIP_HEIGHT = 76;

const CATEGORIES = {
  mastered: { key: "mastered", name: "Maîtrisé", color: "#1f6b45" },
  review: { key: "review", name: "À revoir", color: "#991b1b" },
  played12: { key: "played12", name: "Joué 1-2 fois", color: "#3b6ea5" },
  never: { key: "never", name: "Jamais joué", color: "#9a938a" },
};

const HIDDEN_ITEMS_LS_KEY = "vocabularium_matrix_chart_hidden";

function readHiddenItems() {
  try {
    const raw = localStorage.getItem(HIDDEN_ITEMS_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeHiddenItems(hiddenItems) {
  try {
    localStorage.setItem(HIDDEN_ITEMS_LS_KEY, JSON.stringify(hiddenItems));
  } catch {
    // quota / mode privé : on ignore
  }
}

function categoryForGradeIndex(index) {
  // Maîtrisé = grade supérieur ou égal à B+ ; à revoir = tout grade inférieur à B+.
  return index <= IDX_BPLUS ? "mastered" : "review";
}

// Petit générateur pseudo-aléatoire déterministe (pour un jitter stable entre
// deux rendus, afin que les points ne « sautent » pas au survol).
function seededJitter(seed, amplitude) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  const frac = x - Math.floor(x);
  return (frac - 0.5) * 2 * amplitude;
}

function normalizeSearchText(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("fr");
}

function wordsMatchSearch(mot, query) {
  if (!mot || !query) return false;
  return mot.localeCompare(query, "fr", { sensitivity: "accent" }) === 0;
}

function findPointBySearch(allPoints, rawQuery) {
  const query = String(rawQuery || "").trim();
  if (!query) {
    return { error: "Saisissez un mot à rechercher." };
  }

  const exactMatches = allPoints.filter((point) => wordsMatchSearch(point.mot, query));
  if (exactMatches.length === 1) {
    return { point: exactMatches[0] };
  }
  if (exactMatches.length > 1) {
    return { error: "Plusieurs mots correspondent. Précisez la recherche." };
  }

  const normalizedQuery = normalizeSearchText(query);
  const partialMatches = allPoints.filter((point) =>
    normalizeSearchText(point.mot).includes(normalizedQuery)
  );

  if (partialMatches.length === 1) {
    return { point: partialMatches[0] };
  }
  if (partialMatches.length > 1) {
    return { error: "Plusieurs mots correspondent. Précisez la recherche." };
  }

  return { error: "Mot introuvable dans la matrice." };
}

function computePointPixelPosition(point, containerSize, maxGames) {
  if (!point || !containerSize?.width || !containerSize?.height) {
    return null;
  }

  const plotLeft = CHART_MARGIN.left + Y_AXIS_WIDTH;
  const plotTop = CHART_MARGIN.top;
  const plotWidth = containerSize.width - plotLeft - CHART_MARGIN.right;
  const plotHeight = containerSize.height - plotTop - CHART_MARGIN.bottom;

  if (plotWidth <= 0 || plotHeight <= 0) {
    return null;
  }

  const xDomainMin = -0.4;
  const xDomainMax = maxGames + 0.6;
  const yDomainMin = UNRATED_Y - 1;
  const yDomainMax = GRADE_COUNT - 0.4;

  const cx = plotLeft + ((point.x - xDomainMin) / (xDomainMax - xDomainMin)) * plotWidth;
  const cy = plotTop + ((yDomainMax - point.y) / (yDomainMax - yDomainMin)) * plotHeight;

  let tooltipLeft = cx + 12;
  let tooltipTop = cy - PINNED_TOOLTIP_HEIGHT - 8;
  tooltipLeft = Math.min(
    Math.max(plotLeft, tooltipLeft),
    plotLeft + plotWidth - PINNED_TOOLTIP_WIDTH - 4
  );
  tooltipTop = Math.min(
    Math.max(plotTop, tooltipTop),
    plotTop + plotHeight - PINNED_TOOLTIP_HEIGHT - 4
  );

  return {
    cx,
    cy,
    plotLeft,
    plotTop,
    plotWidth,
    plotHeight,
    tooltipLeft,
    tooltipTop,
  };
}

function MatrixLegend({ items, hiddenItems, onToggle }) {
  return (
    <div className="mastery-matrix__legend" role="list" aria-label="Légende de la matrice">
      {items.map((item) => {
        const isHidden = Boolean(hiddenItems[item.key]);
        return (
          <button
            type="button"
            key={item.key}
            className={`mastery-matrix__legend-item${
              isHidden ? " mastery-matrix__legend-item--hidden" : ""
            }`}
            aria-pressed={!isHidden}
            onClick={() => onToggle(item.key)}
          >
            <span
              className="mastery-matrix__legend-swatch"
              style={{ background: item.color }}
              aria-hidden="true"
            />
            {item.name}
            <span className="mastery-matrix__legend-count">{item.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function MatrixTooltipContent({ point }) {
  if (!point) return null;

  return (
    <div className="mastery-matrix__tooltip">
      <p className="mastery-matrix__tooltip-word">{point.mot}</p>
      <p className="mastery-matrix__tooltip-line">
        {point.grade
          ? `Grade EWSS : ${point.grade}`
          : point.gameCount === 0
            ? "Jamais joué"
            : "Non évalué"}
      </p>
      <p className="mastery-matrix__tooltip-line">
        {point.gameCount} partie{point.gameCount > 1 ? "s" : ""} jouée
        {point.gameCount > 1 ? "s" : ""}
      </p>
    </div>
  );
}

function MatrixTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return <MatrixTooltipContent point={point} />;
}

export default function MasteryMatrixChart({
  words = [],
  loading = false,
  error = null,
  poolLevel = null,
  poolScope = "cumulative",
  onPoolScopeChange,
}) {
  const chartWrapRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const [hiddenItems, setHiddenItems] = useState(() => readHiddenItems());
  const [searchQuery, setSearchQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [pinnedPoint, setPinnedPoint] = useState(null);

  const { series, maxGames, counts, allPoints } = useMemo(() => {
    const buckets = { mastered: [], review: [], played12: [], never: [] };
    let max = 1;

    (Array.isArray(words) ? words : []).forEach((word, index) => {
      if (!word?.mot) return;
      const gameCount = Number(word?.stats?.gameCount) || 0;
      if (gameCount > max) max = gameCount;

      const grade = getWordEwssDisplayGrade(word);
      let category;
      let yBase;
      if (grade) {
        const gradeIndex = EWSS_GRADES.indexOf(grade);
        category = categoryForGradeIndex(gradeIndex);
        // A+ (index 0) en haut, D- (index 11) en bas.
        yBase = GRADE_COUNT - 1 - gradeIndex;
      } else {
        category = gameCount === 0 ? "never" : "played12";
        yBase = UNRATED_Y;
      }

      buckets[category].push({
        mot: word.mot,
        grade: grade || null,
        gameCount,
        category,
        color: CATEGORIES[category].color,
        // x >= 0 : « parties jouées » ne peut pas être négatif.
        x: Math.max(0, gameCount + seededJitter(index + 1, 0.26)),
        y: yBase + seededJitter((index + 1) * 7.3, 0.34),
      });
    });

    const flatPoints = Object.values(buckets).flat();

    return {
      series: buckets,
      maxGames: max,
      allPoints: flatPoints,
      counts: {
        mastered: buckets.mastered.length,
        review: buckets.review.length,
        played12: buckets.played12.length,
        never: buckets.never.length,
      },
    };
  }, [words]);

  const pinnedLayout = useMemo(
    () => computePointPixelPosition(pinnedPoint, chartSize, maxGames),
    [pinnedPoint, chartSize, maxGames]
  );

  useEffect(() => {
    const element = chartWrapRef.current;
    if (!element) return undefined;

    const updateSize = () => {
      setChartSize({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, [words.length, loading, error, pinnedPoint]);

  const legendItems = useMemo(
    () =>
      Object.values(CATEGORIES).map((cat) => ({
        ...cat,
        count: counts[cat.key] ?? 0,
      })),
    [counts]
  );

  const handleLegendToggle = (itemKey) => {
    setHiddenItems((prev) => {
      const next = { ...prev, [itemKey]: !prev[itemKey] };
      writeHiddenItems(next);
      return next;
    });
  };

  const handlePinSearch = useCallback(() => {
    const result = findPointBySearch(allPoints, searchQuery);

    if (result.error) {
      setPinnedPoint(null);
      setSearchError(result.error);
      return;
    }

    setSearchError("");
    setPinnedPoint(result.point);
  }, [allPoints, searchQuery]);

  const handleResetPin = useCallback(() => {
    setPinnedPoint(null);
    setSearchError("");
  }, []);

  const handleSearchAction = useCallback(() => {
    if (pinnedPoint) {
      handleResetPin();
      return;
    }
    handlePinSearch();
  }, [handlePinSearch, handleResetPin, pinnedPoint]);

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (pinnedPoint) {
          handleResetPin();
        } else {
          handlePinSearch();
        }
      }
    },
    [handlePinSearch, handleResetPin, pinnedPoint]
  );

  if (loading) {
    return (
      <div className="game-progress-chart game-progress-chart--empty">
        <p className="game-progress-chart__empty-title">Chargement de la matrice…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-progress-chart game-progress-chart--empty">
        <p className="game-progress-chart__empty-title">Impossible de charger les mots</p>
        <p className="game-progress-chart__empty-text">{error}</p>
      </div>
    );
  }

  const totalWords =
    counts.mastered + counts.review + counts.played12 + counts.never;

  if (totalWords === 0) {
    return (
      <div className="game-progress-chart game-progress-chart--empty">
        <p className="game-progress-chart__empty-title">Aucun mot à afficher</p>
        <p className="game-progress-chart__empty-text">
          La matrice apparaîtra dès que des mots seront enregistrés.
        </p>
      </div>
    );
  }

  const gradeTicks = Array.from({ length: GRADE_COUNT }, (_, i) => i);
  const xTickStep = maxGames > 12 ? Math.ceil(maxGames / 10) : 1;
  const gameTicks = [];
  for (let t = 0; t <= maxGames; t += xTickStep) gameTicks.push(t);

  return (
    <div className="mastery-matrix-root">
      <section
        className="game-progress-chart mastery-matrix"
        aria-label="Matrice de maîtrise : un point par mot, selon son grade EWSS et son nombre de parties jouées"
      >
      <div className="mastery-matrix__legend-row">
        <MatrixLegend
          items={legendItems}
          hiddenItems={hiddenItems}
          onToggle={handleLegendToggle}
        />
        {poolLevel && onPoolScopeChange && (
          <MatrixPoolScopeDropdown
            level={poolLevel}
            value={poolScope}
            onChange={onPoolScopeChange}
          />
        )}
      </div>
      <div
        className="game-progress-chart__chart-wrap mastery-matrix__chart-wrap"
        ref={chartWrapRef}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(57, 33, 13, 0.12)" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[-0.4, maxGames + 0.6]}
              ticks={gameTicks}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#39210d" }}
              stroke="rgba(57, 33, 13, 0.4)"
            />
            <YAxis
              type="number"
              dataKey="y"
              name="Grade EWSS"
              domain={[UNRATED_Y - 1, GRADE_COUNT - 0.4]}
              ticks={gradeTicks}
              interval={0}
              tick={{ fontSize: 11, fill: "#39210d" }}
              stroke="rgba(57, 33, 13, 0.4)"
              tickFormatter={(value) => EWSS_GRADES[GRADE_COUNT - 1 - value] || ""}
              width={Y_AXIS_WIDTH}
            />
            <ZAxis type="number" range={[16, 16]} />
            <ReferenceLine
              y={(UNRATED_Y + 0) / 2}
              stroke="rgba(57, 33, 13, 0.25)"
              strokeDasharray="4 4"
            />
            {pinnedPoint ? (
              <>
                <ReferenceLine
                  x={pinnedPoint.x}
                  stroke={PINNED_CURSOR_STROKE}
                  strokeDasharray={PINNED_CURSOR_DASH}
                  ifOverflow="extendDomain"
                />
                <ReferenceLine
                  y={pinnedPoint.y}
                  stroke={PINNED_CURSOR_STROKE}
                  strokeDasharray={PINNED_CURSOR_DASH}
                  ifOverflow="extendDomain"
                />
                <ReferenceDot
                  x={pinnedPoint.x}
                  y={pinnedPoint.y}
                  r={9}
                  fill={pinnedPoint.color || "#39210d"}
                  fillOpacity={0.95}
                  stroke="#39210d"
                  strokeWidth={1.25}
                  isFront
                />
              </>
            ) : null}
            <Tooltip
              active={pinnedPoint ? false : undefined}
              content={<MatrixTooltip />}
              cursor={{ strokeDasharray: PINNED_CURSOR_DASH, stroke: PINNED_CURSOR_STROKE }}
            />
            {Object.values(CATEGORIES).map((cat) =>
              hiddenItems[cat.key] ? null : (
                <Scatter
                  key={cat.key}
                  name={cat.name}
                  data={series[cat.key]}
                  fill={cat.color}
                  fillOpacity={0.62}
                  isAnimationActive={false}
                />
              )
            )}
          </ScatterChart>
        </ResponsiveContainer>

        {pinnedPoint && pinnedLayout ? (
          <div className="mastery-matrix__pinned-layer" aria-hidden="true">
            <div
              className="mastery-matrix__pinned-tooltip"
              style={{
                left: `${pinnedLayout.tooltipLeft}px`,
                top: `${pinnedLayout.tooltipTop}px`,
                width: `${PINNED_TOOLTIP_WIDTH}px`,
              }}
            >
              <MatrixTooltipContent point={pinnedPoint} />
            </div>
          </div>
        ) : null}
      </div>
      </section>

      <div className="mastery-matrix__search">
        <label className="mastery-matrix__search-label" htmlFor="mastery-matrix-search-input">
          Rechercher un mot
        </label>
        <div className="mastery-matrix__search-row">
          <input
            id="mastery-matrix-search-input"
            type="search"
            className="mastery-matrix__search-input"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              if (searchError) setSearchError("");
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="mot à localiser"
            autoComplete="off"
            spellCheck={false}
            disabled={Boolean(pinnedPoint)}
          />
          <button
            type="button"
            className="mastery-matrix__search-btn"
            onClick={handleSearchAction}
            aria-label={pinnedPoint ? "Rétablir la matrice" : "Voir le mot dans la matrice"}
          >
            <span className="mastery-matrix__search-btn-label">
              {pinnedPoint ? "Rétablir" : "Voir"}
            </span>
          </button>
        </div>
        <div className="mastery-matrix__search-feedback" aria-live="polite">
          {searchError ? (
            <p className="mastery-matrix__search-error" role="alert">
              {searchError}
            </p>
          ) : pinnedPoint ? (
            <p className="mastery-matrix__search-status">
              Point figé sur <strong>{pinnedPoint.mot}</strong>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
